/**
 * Affiliate portal API client — a SEPARATE sign-in from the customer app, with its own token
 * (localStorage "rankbist_aff_token"). Used only under /affiliate.
 */
const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:8000";
const TOKEN_KEY = "rankbist_aff_token";
// The portal's last picture (balance, link, counts), so it opens at once next time — even on a slow connection —
// and is refreshed straight away. Tied to this sign-in; removed on sign-out.
const SNAPSHOT_KEY = "rankbist_aff_snapshot";

export function affToken(): string | null {
  if (typeof window === "undefined") return null;
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setAffToken(t: string) {
  try { localStorage.setItem(TOKEN_KEY, t); localStorage.removeItem(SNAPSHOT_KEY); } catch { /* blocked */ }
  inflight.clear();
  // Just signed in (login, Google, verify): the portal opens next, so its numbers start loading now, while the
  // page code is still on its way. The portal page joins these same requests.
  if (typeof window !== "undefined") window.setTimeout(warmAffPortal, 0);
}
export function clearAffToken() {
  try { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(SNAPSHOT_KEY); } catch { /* blocked */ }
  inflight.clear();
}

// Two parts of the page (or the landing's warm-up and the portal) asking for the same read at once share one request.
const inflight = new Map<string, Promise<unknown>>();

function req<T>(path: string, init?: RequestInit, auth = true): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  if (method !== "GET" || typeof window === "undefined") return send<T>(path, init, auth);
  const key = `${auth ? affToken() ?? "" : ""}|${path}`;
  const pending = inflight.get(key);
  if (pending) return pending as Promise<T>;
  const p = send<T>(path, init, auth).finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

async function send<T>(path: string, init?: RequestInit, auth = true): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json", ...(init?.headers as Record<string, string>) };
  if (auth) {
    const t = affToken();
    if (t) headers.Authorization = `Bearer ${t}`;
  }
  const method = (init?.method ?? "GET").toUpperCase();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  } catch {
    // A weak connection drops a request now and then: a read is asked once more before giving up. Changes never are.
    const online = typeof navigator === "undefined" || navigator.onLine !== false;
    if (method !== "GET" || !online) throw new Error(online ? "Couldn't reach Rankbist. Check your connection and try again." : "You're offline. Check your connection and try again.");
    await new Promise((r) => setTimeout(r, 900));
    try {
      res = await fetch(`${API_BASE}${path}`, { ...init, headers });
    } catch {
      throw new Error("Couldn't reach Rankbist. Check your connection and try again.");
    }
  }
  if (!res.ok) {
    let detail = `Something went wrong (${res.status}).`;
    try {
      const body = await res.json();
      if (typeof body?.detail === "string") detail = body.detail;
    } catch { /* non-JSON */ }
    const e = new Error(detail) as Error & { status?: number };
    e.status = res.status;
    throw e;
  }
  if (res.status === 204) return {} as T;
  return res.json();
}

export interface AffStats {
  link: string; min_withdraw_cents: number; rates: { first: number; upgrade: number; renewal: number };
  clicks: number; signups: number; buyers: number; by_plan: Record<string, number>;
  available_cents: number; pending_cents?: number; hold_days?: number; paid_cents: number; pending_withdraw_cents: number; lifetime_cents: number; currency: string;
}
export interface AffCommission {
  id: string; kind: string; plan: string | null; gross_cents: number; rate_bps: number;
  amount_cents: number; currency: string; status: string; at: string;
}
export interface AffPayoutMethod { id: string; type: string; label: string; details: Record<string, string> }
export interface AffWithdrawal {
  id: string; amount_cents: number; currency: string; method: string; status: string; note: string;
  requested_at: string; resolved_at: string | null;
}

export const affApi = {
  terms: () => req<{ open: boolean; rate_first_bps: number; rate_upgrade_bps: number; rate_renewal_bps: number; min_withdraw_cents: number; hold_days?: number }>("/api/affiliate/settings", undefined, false),
  signup: (name: string, email: string, password: string) =>
    req<{ requires_verification: boolean }>("/api/affiliate/signup", { method: "POST", body: JSON.stringify({ name, email, password }) }, false),
  verify: (email: string, code: string) =>
    req<{ token: string }>("/api/affiliate/verify", { method: "POST", body: JSON.stringify({ email, code }) }, false),
  login: (email: string, password: string) =>
    req<{ token: string }>("/api/affiliate/login", { method: "POST", body: JSON.stringify({ email, password }) }, false),
  google: (id_token: string) =>
    req<{ token: string }>("/api/affiliate/google", { method: "POST", body: JSON.stringify({ id_token }) }, false),
  resend: (email: string) => req<{ message: string }>("/api/affiliate/resend", { method: "POST", body: JSON.stringify({ email }) }, false),
  forgotPassword: (email: string) =>
    req<{ message: string }>("/api/affiliate/forgot-password", { method: "POST", body: JSON.stringify({ email }) }, false),
  resetPassword: (email: string, code: string, new_password: string) =>
    req<{ message: string }>("/api/affiliate/reset-password", { method: "POST", body: JSON.stringify({ email, code, new_password }) }, false),
  me: () => req<{ id: string; email: string; name: string | null; code: string; link: string }>("/api/affiliate/me"),
  dashboard: () => req<AffStats>("/api/affiliate/dashboard"),
  commissions: () => req<{ commissions: AffCommission[] }>("/api/affiliate/commissions"),
  methods: () => req<{ methods: AffPayoutMethod[] }>("/api/affiliate/payout-methods"),
  addMethod: (type: string, label: string, details: Record<string, string>) =>
    req<{ id: string }>("/api/affiliate/payout-methods", { method: "POST", body: JSON.stringify({ type, label, details }) }),
  withdraw: (method_id: string) => req<{ id: string; message: string }>("/api/affiliate/withdraw", { method: "POST", body: JSON.stringify({ method_id }) }),
  withdrawals: () => req<{ withdrawals: AffWithdrawal[] }>("/api/affiliate/withdrawals"),
};

export function money(cents: number | null | undefined, currency = "USD"): string {
  if (cents == null || !Number.isFinite(cents)) return "—";
  try { return (cents / 100).toLocaleString(undefined, { style: "currency", currency }); }
  catch { return `$${(cents / 100).toFixed(2)}`; }
}

export type AffMe = { id: string; email: string; name: string | null; code: string; link: string };
export type AffSnapshot = { me: AffMe | null; stats: AffStats; at: number };

function tokenId(): string {
  const t = affToken() ?? "";
  let h = 0;
  for (let i = 0; i < t.length; i++) h = (Math.imul(31, h) + t.charCodeAt(i)) | 0;
  return t ? `${t.length}:${(h >>> 0).toString(36)}` : "";
}

/** The last portal picture for this sign-in (at most 30 days old), or null. */
export function readAffSnapshot(): AffSnapshot | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(SNAPSHOT_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as AffSnapshot & { id?: string };
    if (!s?.stats || s.id !== tokenId() || !tokenId() || Date.now() - (s.at ?? 0) > 30 * 86_400_000) return null;
    return { me: s.me ?? null, stats: s.stats, at: s.at };
  } catch {
    return null;
  }
}

export function writeAffSnapshot(stats: AffStats, me: AffMe | null) {
  try {
    const prev = readAffSnapshot();
    localStorage.setItem(SNAPSHOT_KEY, JSON.stringify({ id: tokenId(), at: Date.now(), stats, me: me ?? prev?.me ?? null }));
  } catch { /* storage full or blocked */ }
}

/**
 * Start loading the portal before it is opened (the landing page calls this when this browser is signed in, and
 * again when the pointer reaches the button), so the portal usually has its numbers by the time it appears.
 * The requests are shared with the portal page, never sent twice.
 */
export function warmAffPortal() {
  if (typeof window === "undefined" || !affToken()) return;
  Promise.all([affApi.dashboard(), affApi.me().catch(() => null)])
    .then(([stats, me]) => writeAffSnapshot(stats, me))
    .catch(() => undefined);
}
